import { resolveAudience } from './audience'

export type Answer = string | number | boolean | string[]
export type FunnelSnapshot = {
  session: { id: string; status: string; locale: 'en' | 'el'; termsVersion: string | null; privacyVersion: string | null; agreementAcceptedAt: string | null; researchConsent: boolean | null; claimedAt: string | null }
  responses: Record<string, Answer>
  experiments: Record<string, string>
}
export type FunnelResult = {
  title: string
  recommendedDeck: string
  questionMix: { light: number; playful: number; meaningful: number }
  samplePrompt: string
  pace: string
  durationMinutes: number
  /** Reflect-back payload — what the user told us, rendered back in their terms. */
  reflect: {
    context: string
    goal: string
    minutes: number
    pace: string
    depth: number
    boundaryCount: number
    barrier: string | null
  }
}
/** Server-owned funnel configuration. `mode` decides whether we sell or collect. */
export type FunnelConfig = {
  mode: 'waitlist' | 'paid'
  deckOffer: { enabled: boolean; url: string | null; amount: number | null; currency: string }
  appStoreUrl: string | null
}
export type WaitlistPlacement = { joined: boolean; position: number | null; alreadyOnList: boolean }
export type AuthResponse = { accessToken: string; refreshToken: string; user: { id: string; email: string; username?: string | null } }

const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3001/v1').replace(/\/$/, '')
const AUTH_KEY = 'bonded_funnel_auth'

export class ApiError extends Error {
  readonly status: number
  readonly code?: string
  constructor(message: string, status: number, code?: string) { super(message); this.status = status; this.code = code }
}

async function request<T>(path: string, init: RequestInit = {}, authenticated = false): Promise<T> {
  const auth = authenticated ? getAuth() : null
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(auth ? { Authorization: `Bearer ${auth.accessToken}` } : {}), ...init.headers },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string; code?: string }
    throw new ApiError(body.error || 'Something went wrong', response.status, body.code)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export function getOrCreateSessionId() {
  const existing = sessionStorage.getItem('bonded_funnel_session_id')
  if (existing) return existing
  const id = crypto.randomUUID()
  sessionStorage.setItem('bonded_funnel_session_id', id)
  return id
}
export function replaceSessionId() {
  const id = crypto.randomUUID()
  sessionStorage.setItem('bonded_funnel_session_id', id)
  return id
}
export const initialSessionId = getOrCreateSessionId()

/** Bump in lockstep with FUNNEL_CONSENT_VERSION in apps/api/src/services/funnel.ts. */
const CONSENT_VERSION = '2026-07-28'

/**
 * The Terms and Privacy versions this build was written against. The API
 * rejects an agreement that names any other version, which is deliberate: a
 * stale bundle served from a CDN cache must not be able to record acceptance of
 * text the user was never shown (GDPR Art. 7(1)).
 *
 * Bump in lockstep with TERMS_OF_SERVICE_VERSION / PRIVACY_POLICY_VERSION in
 * apps/api/src/legal/document-versions.ts.
 */
const TERMS_VERSION = '2026-08-25'
const PRIVACY_VERSION = '2026-08-25'

/** Click ids we forward into the session attribution, alongside every utm_*. */
const CLICK_ID_PARAMS = ['ttclid', 'tt_test_id']

function entryAttribution(): Record<string, string> {
  const params = new URLSearchParams(location.search)
  const attribution: Record<string, string> = { app: resolveAudience() }
  for (const [key, value] of params) {
    if (key.startsWith('utm_') || CLICK_ID_PARAMS.includes(key)) attribution[key] = value
  }
  return attribution
}

export const funnelApi = {
  create: (sessionId: string, locale: string) => request<{ sessionId: string; experiments: Record<string, string> }>('/funnel/sessions', { method: 'POST', body: JSON.stringify({ sessionId, locale, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, referrer: document.referrer, attribution: entryAttribution() }) }),
  get: (id: string) => request<FunnelSnapshot>(`/funnel/sessions/${id}`),
  config: () => request<FunnelConfig>('/funnel/config'),
  agreement: (id: string) => request<{ accepted: boolean }>(`/funnel/sessions/${id}/agreement`, { method: 'PATCH', body: JSON.stringify({ termsVersion: TERMS_VERSION, privacyVersion: PRIVACY_VERSION }) }),
  // Research participation is now an explicit yes OR no, recorded upfront on the
  // landing screen. A decline is meaningfully different from "never asked".
  setResearch: (id: string, participating: boolean) => request<{ saved: boolean; participating: boolean }>(`/funnel/sessions/${id}/research`, { method: 'PATCH', body: JSON.stringify({ participating, noticeVersion: CONSENT_VERSION }) }),
  // Waitlist lead capture. Unauthenticated: while the app is private the funnel
  // sells nothing, so the browser never needs an access token.
  lead: (id: string, email: string, displayName?: string) => request<WaitlistPlacement>(`/funnel/sessions/${id}/lead`, { method: 'POST', body: JSON.stringify({ email, displayName }) }),
  answer: (id: string, questionKey: string, answer: Answer) => request(`/funnel/sessions/${id}/responses/${questionKey}`, { method: 'PUT', body: JSON.stringify({ answer }), headers: { 'Idempotency-Key': crypto.randomUUID() } }),
  event: (id: string, eventName: string, properties: Record<string, string | number | boolean> = {}) => request(`/funnel/sessions/${id}/events`, { method: 'POST', body: JSON.stringify({ eventId: crypto.randomUUID(), eventName, occurredAt: new Date().toISOString(), properties }) }),
  result: (id: string) => request<FunnelResult>(`/funnel/sessions/${id}/result`, { method: 'POST', body: '{}' }),
  claim: (id: string, displayName?: string) => request<{ claimed: boolean; waitlist: { position: number; alreadyOnList: boolean } | null }>('/me/funnel/claim', { method: 'POST', body: JSON.stringify({ sessionId: id, displayName }) }, true),
}

export interface PaymentsCatalog {
  environment: 'sandbox' | 'production'
  checkoutEnabled: boolean
  clientToken: string
  premium: { priceId: string; discountId: string | null; durationDays: number }
}
export interface GiftRedeemResponse {
  gift: { label: string; durationDays: number | null; isLifetime: boolean }
  premium: { isActive: boolean; expiresAt: string | null }
}
export const paymentsApi = {
  catalog: () => request<PaymentsCatalog>('/payments/catalog'),
  // Authenticated (the visitor just created an account at /claim): create a
  // Paddle transaction for the 20%-off premium pass, bound to the user.
  premiumCheckout: () => request<{ transactionId: string }>('/payments/premium-checkout', { method: 'POST', body: '{}' }, true),
  redeemGift: (code: string) => request<GiftRedeemResponse>('/gifts/redeem', { method: 'POST', body: JSON.stringify({ code }) }, true),
}

/** Report a failed funnel action so "we couldn't save your ritual" becomes measurable.
 * Records the HTTP status/code and connectivity as a `client_error` funnel event.
 * Best-effort: if the failure is itself an auth (401) or origin (403) problem, this
 * event will be rejected too — those surface as a missing `answer_saved` rather than a
 * `client_error`, which is itself a useful signal (session/CORS vs server/validation). */
export function reportClientError(step: string, error: unknown) {
  const errorCode = error instanceof ApiError ? error.code || `HTTP_${error.status}` : 'NETWORK'
  const networkState = typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'online'
  void funnelApi.event(initialSessionId, 'client_error', { step: step.slice(0, 80), errorCode, networkState }).catch(() => {})
}

export function eventOnce(id: string, eventName: string, properties: Record<string, string | number | boolean> = {}) {
  const fingerprint = `${eventName}:${JSON.stringify(properties)}`
  const key = `bonded_funnel_event:${id}:${fingerprint}`
  if (sessionStorage.getItem(key)) return Promise.resolve()
  sessionStorage.setItem(key, 'sent')
  return funnelApi.event(id, eventName, properties).catch((error) => {
    sessionStorage.removeItem(key)
    throw error
  })
}

export function setAuth(auth: AuthResponse) { sessionStorage.setItem(AUTH_KEY, JSON.stringify(auth)) }
export function getAuth(): AuthResponse | null { try { return JSON.parse(sessionStorage.getItem(AUTH_KEY) || 'null') as AuthResponse | null } catch { return null } }
export const authApi = {
  google: (idToken: string) => request<AuthResponse>('/auth/google', { method: 'POST', body: JSON.stringify({ idToken }) }),
  requestLink: (email: string, redirect?: string) => request<{ message: string; devToken?: string }>('/auth/request-link', { method: 'POST', body: JSON.stringify({ email, ...(redirect ? { redirect } : {}) }) }),
  verify: (token: string) => request<AuthResponse>(`/auth/verify?token=${encodeURIComponent(token)}`, { headers: {} }),
}
